import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';

/**
 * VerifyEmailPage — handles the email-verification link clicked from the
 * registration verification email. Calls GET /api/auth/verify-email?token=...
 * On success, instructs the user to log in. On expired/invalid token,
 * offers a resend option.
 */
const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying | success | expired | invalid | error
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('No verification token provided.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setStatus('success');
        setMessage(res.data?.message || 'Email verified. You can now log in and start reading.');
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        if (detail?.code === 'TOKEN_EXPIRED') {
          setStatus('expired');
          setMessage(detail.message || 'This verification link has expired.');
        } else {
          setStatus('invalid');
          setMessage(typeof detail === 'string' ? detail : (detail?.message || 'Invalid or already-used verification link.'));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail || !resendEmail.includes('@')) {
      setResendMessage('Please enter a valid email address.');
      return;
    }
    setResendLoading(true);
    setResendMessage(null);
    try {
      await api.post('/auth/resend-verification', { email: resendEmail.trim().toLowerCase() });
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } catch (e) {
      setResendMessage('If an unverified account exists, a new verification link has been sent.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <img
            src="/newaudia-logo.jpg"
            alt="NewAudia"
            className="h-12 w-auto mx-auto rounded-lg shadow mb-4"
          />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Email Verification
          </h1>
        </div>

        {status === 'verifying' && (
          <div className="text-center py-6" data-testid="verify-email-loading">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-700">Verifying your email…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center py-4" data-testid="verify-email-success">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-2">You're verified!</h2>
            <p className="text-sm text-gray-700 mb-5">{message}</p>
            <Button
              data-testid="verify-email-go-to-login-btn"
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
            >
              Go to Sign In
            </Button>
          </div>
        )}

        {(status === 'expired' || status === 'invalid') && (
          <div className="text-center py-2" data-testid="verify-email-failure">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {status === 'expired' ? 'Link expired' : 'Verification failed'}
            </h2>
            <p className="text-sm text-gray-700 mb-5">{message}</p>

            <div className="text-left bg-gray-50 border border-gray-200 rounded-md p-4 mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Request a new verification link
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  data-testid="verify-email-resend-input"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value.trim().toLowerCase())}
                  placeholder="you@example.com"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Button
                  data-testid="verify-email-resend-btn"
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {resendLoading ? '…' : 'Send'}
                </Button>
              </div>
              {resendMessage && (
                <p className="text-xs text-gray-600 mt-2" data-testid="verify-email-resend-message">
                  {resendMessage}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-xs text-purple-700 underline"
              data-testid="verify-email-back-to-login"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
